// Servicio de API para Gold Web — alineado con Swagger / backend Django

const DEFAULT_API_ROOT = "https://dev.goldloterias.com/api";

function resolveProxyUpstream() {
  const explicit = process.env.REACT_APP_API_PROXY_UPSTREAM;
  if (explicit && String(explicit).trim()) {
    return String(explicit).trim().replace(/\/$/, "");
  }
  const root = process.env.REACT_APP_API_ROOT;
  if (root && String(root).trim()) {
    try {
      return new URL(String(root).trim()).origin;
    } catch {
      /* ignore */
    }
  }
  return "https://dev.goldloterias.com";
}

/** Destino del proxy local / rewrite (mismo host que REACT_APP_API_ROOT si está definido). */
export const API_PROXY_UPSTREAM = resolveProxyUpstream();

function isLocalDevHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname || "";
  return h === "localhost" || h === "127.0.0.1";
}

/**
 * Front desplegado (Vercel, goldloterias.com, etc.): proxy same-origin vía función
 * serverless /api/proxy (ver api/proxy/[...path].js). /api-proxy redirige ahí por vercel.json.
 * En localhost + npm start se usa /api vía src/setupProxy.js (lee REACT_APP_API_ROOT).
 */
function shouldUseProductionProxy() {
  if (process.env.REACT_APP_FORCE_API_PROXY === "true") return true;
  if (isLocalDevHost()) return false;
  if (process.env.NODE_ENV === "development") return false;
  return true;
}

function resolveApiRoot() {
  let env = process.env.REACT_APP_API_ROOT;
  if (env) env = String(env).trim();

  // Local: siempre /api (same-origin) → setupProxy → host de REACT_APP_API_ROOT (sin CORS).
  if (process.env.NODE_ENV === "development" && isLocalDevHost()) {
    return "/api";
  }

  if (shouldUseProductionProxy()) {
    if (!env || /^https?:\/\//i.test(env)) {
      return "/api/proxy";
    }
    return env;
  }

  if (env) {
    if (
      typeof window !== "undefined" &&
      window.location.protocol === "https:" &&
      env.startsWith("http://")
    ) {
      return env.replace(/^http:\/\//i, "https://");
    }
    return env;
  }

  return DEFAULT_API_ROOT;
}

const API_ROOT = resolveApiRoot();

/** Longitud por defecto del PIN (la más segura; alineada con la app móvil). */
export const PIN_LENGTH = 6;
/** PIN corto: también permitido en registro, login, cambio y recuperación. */
export const PIN_LENGTH_LEGACY = 4;
/** Longitudes de PIN que el usuario puede elegir (toggle "4 / 6 dígitos"). */
export const PIN_LENGTHS = [PIN_LENGTH_LEGACY, PIN_LENGTH];

/** localStorage: última longitud de PIN usada en este dispositivo. */
const PIN_LENGTH_STORAGE_KEY = "gold.pinLength";

/** Normaliza a 4 o 6; cualquier otro valor → `fallback`. */
export function normalizePinLength(value, fallback = PIN_LENGTH) {
  const n = Number(value);
  return PIN_LENGTHS.includes(n) ? n : fallback;
}

/** Última longitud de PIN usada en este dispositivo (6 si no se conoce). */
export function getStoredPinLength() {
  try {
    return normalizePinLength(window.localStorage.getItem(PIN_LENGTH_STORAGE_KEY));
  } catch {
    return PIN_LENGTH;
  }
}

/** Recuerda la longitud del PIN tras un login/registro/cambio/reset exitoso. */
export function rememberPinLength(length) {
  if (!PIN_LENGTHS.includes(Number(length))) return;
  try {
    window.localStorage.setItem(PIN_LENGTH_STORAGE_KEY, String(Number(length)));
  } catch {
    /* almacenamiento no disponible (modo privado, bloqueado): se ignora */
  }
}

/** Arreglo vacío para PinInput con la longitud indicada. */
export function emptyPinArray(length = PIN_LENGTH) {
  return Array(normalizePinLength(length)).fill("");
}

/**
 * PIN trivial (mismas reglas que el backend): todos los dígitos iguales
 * o secuencia consecutiva ascendente/descendente (1234, 123456, 654321).
 */
export function isTrivialPin(pinString) {
  const s = pinString || "";
  if (!/^\d+$/.test(s) || s.length < 2) return false;
  if (new Set(s).size === 1) return true;
  const d = s.split("").map(Number);
  let asc = true;
  let desc = true;
  for (let i = 0; i < d.length - 1; i += 1) {
    if (d[i + 1] - d[i] !== 1) asc = false;
    if (d[i] - d[i + 1] !== 1) desc = false;
  }
  return asc || desc;
}

/**
 * Valida un PIN NUEVO (registro, cambio, recuperación) con la longitud elegida.
 * Devuelve un mensaje de error amigable o "" si es válido.
 */
export function validateNewPin(pinString, length) {
  const s = pinString || "";
  if (!/^\d*$/.test(s)) return "El PIN solo puede contener números";
  if (s.length !== length) {
    return `Completa tu PIN de ${length} dígitos`;
  }
  if (new Set(s).size === 1) {
    return "Tu PIN no puede tener todos los dígitos iguales (ej: 1111). Elige uno más seguro.";
  }
  if (isTrivialPin(s)) {
    return "Tu PIN no puede ser una secuencia consecutiva (ej: 1234 o 654321). Elige uno más seguro.";
  }
  return "";
}

/** Dígitos consecutivos desde el inicio (ignora celdas vacías al final). */
export function pinDigits(pinArray) {
  const digits = [];
  for (const d of pinArray || []) {
    if (!d) break;
    digits.push(d);
  }
  return digits.join("");
}

/** Login / PIN actual: acepta 4 (legacy) o 6. */
export function isFlexiblePinValid(pinString) {
  const len = (pinString || "").length;
  return len === PIN_LENGTH_LEGACY || len === PIN_LENGTH;
}

/** Raíz API resuelta (absoluta http(s) en local o relativa /api/proxy en Vercel). */
export function getApiRoot() {
  return API_ROOT;
}

export const AUTH_EXPIRED_EVENT = "gold-auth-expired";

const getToken = () => sessionStorage.getItem("accessToken");
const getRefreshToken = () => sessionStorage.getItem("refreshToken");

const authHeaders = () => ({
  "Content-Type": "application/json",
  ...(getToken() && { Authorization: `Bearer ${getToken()}` }),
});

/** Igual que Flutter `IdempotencyKey.generate()` */
export function generateIdempotencyKey() {
  const ts = Date.now() * 1000;
  const suffix = Array.from({ length: 8 }, () =>
    Math.floor(Math.random() * 256)
      .toString(16)
      .padStart(2, "0")
  ).join("");
  return `gold-${ts}-${suffix}`;
}

/** @param {Response} response */
export async function parseJsonSafe(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

function persistSession(access, refresh, user) {
  if (access) sessionStorage.setItem("accessToken", access);
  if (refresh) sessionStorage.setItem("refreshToken", refresh);
  if (user) sessionStorage.setItem("user", JSON.stringify(user));
}

function clearSessionStorage() {
  sessionStorage.removeItem("accessToken");
  sessionStorage.removeItem("refreshToken");
  sessionStorage.removeItem("user");
}

/**
 * Normaliza respuesta de login (varía entre versiones: tokens anidados o planos).
 * @param {object} data
 */
export function normalizeLoginResponse(data) {
  if (!data || typeof data !== "object") {
    return { user: null, access: null, refresh: null };
  }

  const tokens = data.tokens || {};
  const access =
    tokens.access ||
    data.access ||
    data.access_token ||
    data.token ||
    null;
  const refresh =
    tokens.refresh ||
    data.refresh ||
    data.refresh_token ||
    null;

  let user = data.user || null;

  if (!user && data.id) {
    user = { ...data };
    delete user.tokens;
    delete user.access;
    delete user.refresh;
    delete user.refresh_token;
    delete user.access_token;
  }

  return { user, access, refresh };
}

const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  const tryBody = async (body) => {
    const response = await fetch(`${API_ROOT}/auth/token/refresh/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await parseJsonSafe(response);
    if (response.ok && data?.access) {
      sessionStorage.setItem("accessToken", data.access);
      if (data.refresh) sessionStorage.setItem("refreshToken", data.refresh);
      return data.access;
    }
    return null;
  };

  try {
    let access = await tryBody({ refresh: refreshToken });
    if (!access) access = await tryBody({ refresh_token: refreshToken });
    return access;
  } catch (error) {
    console.error("Error refreshing token:", error);
    return null;
  }
};

/**
 * Fetch autenticado con reintento tras 401 y refresco de token.
 * Si `body` es FormData, no se fuerza Content-Type (el browser pone el boundary).
 */
export const fetchWithAuth = async (url, options = {}) => {
  const { headers: optionHeaders, body, ...rest } = options;
  const baseHeaders = { ...authHeaders() };
  if (body instanceof FormData) {
    delete baseHeaders["Content-Type"];
  }
  const mergedHeaders = {
    ...baseHeaders,
    ...(optionHeaders || {}),
  };

  let response = await fetch(url, {
    ...rest,
    body,
    headers: mergedHeaders,
  });

  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      const retryHeaders = { ...authHeaders() };
      if (body instanceof FormData) {
        delete retryHeaders["Content-Type"];
      }
      response = await fetch(url, {
        ...rest,
        body,
        headers: {
          ...retryHeaders,
          ...(optionHeaders || {}),
        },
      });
    } else {
      clearSessionStorage();
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    }
  }

  return response;
};

// ================== AUTH ==================

export const authService = {
  login: async (phone_number, pin) => {
    const response = await fetch(`${API_ROOT}/auth/login/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone_number, pin }),
    });

    const data = await parseJsonSafe(response);
    const { user, access, refresh } = normalizeLoginResponse(data || {});

    if (response.ok && access) {
      persistSession(access, refresh, user);
    } else if (response.ok && user && !access) {
      // Login sin JWT en contrato Swagger antiguo: al menos persistir usuario
      sessionStorage.setItem("user", JSON.stringify(user));
    }

    return { ok: response.ok, data: data || {}, normalized: { user, access, refresh } };
  },

  logout: () => {
    clearSessionStorage();
  },

  isAuthenticated: () => !!getToken(),

  getCurrentUser: () => {
    const user = sessionStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  },
};

// ================== USERS ==================

export const userService = {
  register: async (userData) => {
    const response = await fetch(`${API_ROOT}/users/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(userData),
    });

    const data = await parseJsonSafe(response);
    return { ok: response.ok, data: data || {} };
  },

  getProfile: async (userId) => {
    const response = await fetchWithAuth(`${API_ROOT}/users/${userId}/profile/`);
    const data = await parseJsonSafe(response);
    return { ok: response.ok, data };
  },

  updateProfile: async (userId, userData) => {
    const response = await fetchWithAuth(`${API_ROOT}/users/${userId}/update_profile/`, {
      method: "PATCH",
      body: JSON.stringify(userData),
    });

    const data = await parseJsonSafe(response);

    if (response.ok && data?.user) {
      sessionStorage.setItem("user", JSON.stringify(data.user));
    }

    return { ok: response.ok, data: data || {} };
  },

  /** PATCH multipart (foto, documentos). No fijar Content-Type. */
  updateProfileMultipart: async (userId, formData) => {
    const response = await fetchWithAuth(
      `${API_ROOT}/users/${userId}/update_profile/`,
      {
        method: "PATCH",
        body: formData,
      }
    );

    const data = await parseJsonSafe(response);
    const user =
      data?.user || data?.data?.user || data?.data || (data?.id ? data : null);

    if (response.ok && user && typeof user === "object" && user.id) {
      sessionStorage.setItem("user", JSON.stringify(user));
    }

    return { ok: response.ok, data: data || {}, user };
  },

  /**
   * Verificación de identidad con fotos de la cédula (OCR en backend, ~10-20 s).
   * POST /api/users/{id}/verify_identity/ — multipart: document_front, document_back.
   * fetchWithAuth no fija Content-Type con FormData (el browser pone el boundary).
   * @returns {Promise<{ok: boolean, status: number, code?: string|null, data?: object, message?: string}>}
   */
  verifyIdentity: async (userId, frontFile, backFile) => {
    const fd = new FormData();
    // El backend valida por extensión del nombre (solo .jpg/.jpeg/.png).
    fd.append("document_front", frontFile, identityUploadName(frontFile, "cedula_frente"));
    fd.append("document_back", backFile, identityUploadName(backFile, "cedula_reverso"));

    const response = await fetchWithAuth(
      `${API_ROOT}/users/${userId}/verify_identity/`,
      {
        method: "POST",
        body: fd,
      }
    );

    const data = await parseJsonSafe(response);

    if (response.ok && data?.success !== false) {
      return {
        ok: true,
        status: response.status,
        data: data?.data || data || {},
        message: data?.message || "Identidad verificada exitosamente",
      };
    }

    return {
      ok: false,
      status: response.status,
      code: (data?.error && typeof data.error === "object" && data.error.code) || null,
      message: formatIdentityError(data, response.status),
      data: data || {},
    };
  },

  changePassword: async (userId, oldPin, newPin) => {
    const response = await fetchWithAuth(`${API_ROOT}/users/${userId}/change_password/`, {
      method: "POST",
      body: JSON.stringify({ old_pin: oldPin, new_pin: newPin }),
    });
    const data = await parseJsonSafe(response);
    return { ok: response.ok, data: data || {} };
  },

  /** Forgot PIN — no auth. POST /api/users/request_reset_code/ */
  requestResetCode: async (email) => {
    const response = await fetch(`${API_ROOT}/users/request_reset_code/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const data = await parseJsonSafe(response);
    return { ok: response.ok, data: data || {} };
  },

  /** Forgot PIN — no auth. POST /api/users/verify_reset_code/ */
  verifyResetCode: async (email, code) => {
    const response = await fetch(`${API_ROOT}/users/verify_reset_code/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, code }),
    });
    const data = await parseJsonSafe(response);
    return { ok: response.ok, data: data || {} };
  },

  /** Forgot PIN — no auth. POST /api/users/reset_password/ */
  resetPassword: async (email, code, newPin, confirmPin) => {
    const response = await fetch(`${API_ROOT}/users/reset_password/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        code,
        new_pin: newPin,
        confirm_pin: confirmPin,
      }),
    });
    const data = await parseJsonSafe(response);
    return { ok: response.ok, data: data || {} };
  },

  deleteAccount: async (userId) => {
    const response = await fetchWithAuth(`${API_ROOT}/users/${userId}/`, {
      method: "DELETE",
    });

    return { ok: response.ok };
  },
};

// ================== LOTTERY ==================

function buildQuery(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v) !== "") q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : "";
}

/**
 * Normaliza el JSON de catálogo de loterías (el Swagger a veces documenta otro schema).
 * Flutter espera objetos con id, name, fraction_value, number_fractions, image, series, etc.
 */
export function normalizeLotteryList(payload) {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== "object") return [];

  const keys = ["results", "data", "lotteries", "items", "lottery_list", "lottery_info"];
  for (const k of keys) {
    const v = payload[k];
    if (Array.isArray(v)) return v;
  }

  return [];
}

/** Mensaje de error legible del backend, o `fallback` si no hay uno utilizable. */
function friendlyApiError(data, fallback) {
  if (!data || typeof data !== "object" || data.raw) return fallback;
  if (typeof data.error === "string" && data.error.trim()) return data.error.trim();
  const msg = formatApiError(data);
  return msg && msg !== "Ocurrió un error" ? msg : fallback;
}

export const lotteryService = {
  /**
   * Listado de loterías para jugar / home. Intenta público; si 401 y hay token, reintenta con auth
   * (por si el ALB exige JWT aunque el Swagger marque el recurso como global).
   */
  getLotteryInfo: async () => {
    const url = `${API_ROOT}/lottery/results/lottery_info/`;

    try {
      let response = await fetch(url, {
        headers: { Accept: "application/json" },
        cache: "no-store",
      });

      if (response.status === 401 && getToken()) {
        response = await fetchWithAuth(url, {
          headers: { Accept: "application/json" },
          cache: "no-store",
        });
      }

      const contentType = response.headers.get("content-type") || "";
      if (response.ok && !contentType.includes("application/json")) {
        console.error(
          "[getLotteryInfo] Respuesta no JSON (¿proxy /api/proxy sin desplegar en Vercel?):",
          contentType,
          url
        );
        return {
          ok: false,
          status: response.status,
          data: [],
          raw: { hint: "expected_json", contentType },
        };
      }

      const raw = await parseJsonSafe(response);
      const list = normalizeLotteryList(raw);

      return {
        ok: response.ok,
        status: response.status,
        data: list,
        raw,
      };
    } catch (err) {
      console.error("[getLotteryInfo] Network error:", err);
      return {
        ok: false,
        status: 0,
        data: [],
        raw: { hint: "network_error", message: String(err?.message || err) },
      };
    }
  },

  /**
   * Loterías "Próximamente" (público). No vienen en lottery_info y NO se pueden comprar.
   * GET /api/lottery/results/coming_soon/ → {success, data: [{id, name, image, amount, day, time, coming_soon}]}
   * Nunca lanza: ante error devuelve lista vacía (la UI simplemente no las muestra).
   */
  getComingSoonLotteries: async () => {
    try {
      const response = await fetch(`${API_ROOT}/lottery/results/coming_soon/`, {
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      const contentType = response.headers.get("content-type") || "";
      if (!response.ok || !contentType.includes("application/json")) {
        return { ok: false, status: response.status, data: [] };
      }
      const raw = await parseJsonSafe(response);
      const list = normalizeLotteryList(raw)
        .filter((l) => l && l.id != null)
        .map((l) => ({ ...l, coming_soon: true }));
      return { ok: true, status: response.status, data: list };
    } catch (err) {
      console.error("[getComingSoonLotteries] Network error:", err);
      return { ok: false, status: 0, data: [] };
    }
  },

  /** Resultados persistidos en BD (recomendado para UI) */
  listResults: async (params = {}) => {
    const limit = params.limit ?? 50;
    const qs = buildQuery({ page_size: limit, ...params, limit });
    const response = await fetch(`${API_ROOT}/lottery/results/list_results/${qs}`);
    const data = await parseJsonSafe(response);
    const list = Array.isArray(data)
      ? data
      : Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data?.data)
          ? data.data
          : [];
    return { ok: response.ok, data: list, count: data?.count };
  },

  /** Sincroniza desde S3 (puede ser más lento; opcional) */
  getResultsFromS3: async () => {
    const response = await fetch(`${API_ROOT}/lottery/results/get_result/`);
    const data = await parseJsonSafe(response);
    const list = Array.isArray(data)
      ? data
      : Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data?.data)
          ? data.data
          : [];
    return { ok: response.ok, data: list };
  },

  getBets: async () => {
    const response = await fetchWithAuth(`${API_ROOT}/lottery/bets/`);
    const data = await parseJsonSafe(response);
    return { ok: response.ok, data };
  },

  /**
   * Fracciones disponibles para lottery + number + series (público).
   * GET /api/lottery/bets/check_availability/?lottery=&number=&series=
   */
  checkAvailability: async ({ lottery, number, series }) => {
    const params = new URLSearchParams({
      lottery: String(lottery ?? ""),
      number: String(number ?? ""),
      series: String(series ?? ""),
    });
    const response = await fetch(
      `${API_ROOT}/lottery/bets/check_availability/?${params.toString()}`,
      {
        headers: { Accept: "application/json" },
        cache: "no-store",
      }
    );
    const data = await parseJsonSafe(response);
    if (!response.ok) {
      return { ok: false, status: response.status, data: data || {} };
    }
    return {
      ok: true,
      status: response.status,
      data: {
        lottery: data?.lottery,
        number: data?.number,
        series: data?.series,
        draw_date: data?.draw_date,
        total_fractions: Number(data?.total_fractions) || 0,
        sold_fractions: Number(data?.sold_fractions) || 0,
        available_fractions: Number(data?.available_fractions) || 0,
        is_sold_out: Boolean(data?.is_sold_out),
      },
    };
  },

  /**
   * "Dame suerte": número + serie al azar ENTRE LAS COMBINACIONES CARGADAS de la
   * lotería (no 0000-9999). GET /api/lottery/bets/random_combination/?lottery=
   * 404 si la lotería no tiene emisión cargada o está agotada.
   */
  randomCombination: async ({ lottery }) => {
    const params = new URLSearchParams({ lottery: String(lottery ?? "") });
    const response = await fetch(
      `${API_ROOT}/lottery/bets/random_combination/?${params.toString()}`,
      { headers: { Accept: "application/json" }, cache: "no-store" }
    );
    const data = await parseJsonSafe(response);
    if (!response.ok) {
      return { ok: false, status: response.status, data: data || {} };
    }
    return {
      ok: true,
      status: response.status,
      data: {
        lottery: data?.lottery,
        number: String(data?.number ?? ""),
        series: String(data?.series ?? ""),
        draw_date: data?.draw_date,
        total_fractions: Number(data?.total_fractions) || 0,
        available_fractions: Number(data?.available_fractions) || 0,
      },
    };
  },

  /**
   * Crear apuestas — espejo de Flutter `bet_service.dart` → createBet.
   * Body: [{ lottery, number, series, amount, fractions }, ...]
   * Headers: Authorization Bearer + Idempotency-Key + Content-Type charset UTF-8
   *
   * Bonos y códigos (opcionales, ver docs BONOS_REFERIDOS_PROMOS):
   * - `bonus_id` en cada fila → esa apuesta se paga con el bono ($0).
   * - `promoCode` a nivel de pedido → se envía el sobre `{ bets: [...], promo_code }`.
   * Sin bono ni código el cuerpo es la lista "pelada" de siempre (compatible).
   * @param {object[]} bets  Filas ya en forma API (o se normalizan con buildCreateBetPayload)
   * @param {{ promoCode?: string }} [options]
   */
  createBet: async (bets, { promoCode } = {}) => {
    const list = Array.isArray(bets) ? bets : [bets];
    const rows = list.map((row) => ({
      lottery: row.lottery,
      number: row.number,
      series: row.series,
      amount: row.amount,
      fractions: Number.parseInt(row.fractions, 10) || 1,
      ...(row.bonus_id ? { bonus_id: row.bonus_id } : {}),
      // Geolocalización opcional del lugar de la compra (best-effort).
      ...(typeof row.latitude === "number" &&
      typeof row.longitude === "number"
        ? {
            latitude: row.latitude,
            longitude: row.longitude,
            ...(typeof row.location_accuracy === "number"
              ? { location_accuracy: row.location_accuracy }
              : {}),
            location_source: row.location_source || "gps",
          }
        : {}),
    }));
    const code = String(promoCode || "").trim();
    const body = code ? { bets: rows, promo_code: code } : rows;

    const response = await fetchWithAuth(`${API_ROOT}/lottery/bets/create_bet/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        "Idempotency-Key": generateIdempotencyKey(),
      },
      body: JSON.stringify(body),
    });

    const data = await parseJsonSafe(response);
    const ok = response.status === 200 || response.status === 201;

    if (ok) {
      return { ok: true, status: response.status, data: data || {} };
    }

    // Misma lógica de errores que Flutter bet_service.dart
    if (
      data?.code === "token_not_valid" ||
      (data?.error &&
        typeof data.error === "object" &&
        String(data.error.message || "").toLowerCase().includes("token"))
    ) {
      return {
        ok: false,
        status: response.status,
        data: {
          error: "token_expired",
          message: "Tu sesión ha expirado. Por favor, inicia sesión nuevamente.",
        },
      };
    }

    if (response.status === 403) {
      const errorCode =
        (typeof data?.error === "object" ? data.error.code : data?.error_code) || "";
      if (
        errorCode === "GL-USR-013" ||
        String(data?.message || "").includes("verificar")
      ) {
        return {
          ok: false,
          status: 403,
          data: {
            error: "identity_not_verified",
            message:
              data?.details ||
              data?.message ||
              (typeof data?.error === "object" ? data.error.details || data.error.message : null) ||
              "Debes verificar tu identidad antes de poder apostar. Sube las fotos de tu cédula desde tu perfil.",
          },
        };
      }
    }

    return { ok: false, status: response.status, data: data || {} };
  },

  /**
   * Ticket de una apuesta como imagen PNG (solo el dueño).
   * GET /api/lottery/bets/{bet_id}/ticket/ → image/png
   * @returns {Promise<{ok: boolean, status: number, blob: Blob|null, error?: string}>}
   */
  getBetTicket: async (betId) => {
    try {
      const response = await fetchWithAuth(
        `${API_ROOT}/lottery/bets/${encodeURIComponent(betId)}/ticket/`,
        { headers: { Accept: "image/png" }, cache: "no-store" }
      );
      const contentType = response.headers.get("content-type") || "";
      if (response.ok && contentType.startsWith("image/")) {
        const blob = await response.blob();
        return { ok: true, status: response.status, blob };
      }
      const data = await parseJsonSafe(response);
      return {
        ok: false,
        status: response.status,
        blob: null,
        error: friendlyApiError(
          data,
          response.ok
            ? "La respuesta del servidor no es una imagen válida."
            : "No pudimos cargar el ticket."
        ),
      };
    } catch (err) {
      console.error("[getBetTicket] Network error:", err);
      return {
        ok: false,
        status: 0,
        blob: null,
        error: "Error de conexión. Verifica tu internet e inténtalo de nuevo.",
      };
    }
  },

  /**
   * Envía el ticket de la apuesta al correo del usuario.
   * POST /api/lottery/bets/{bet_id}/send_ticket/ → 200 {success, message} | 429 {success:false, error}
   */
  sendBetTicketEmail: async (betId) => {
    try {
      const response = await fetchWithAuth(
        `${API_ROOT}/lottery/bets/${encodeURIComponent(betId)}/send_ticket/`,
        { method: "POST" }
      );
      const data = await parseJsonSafe(response);
      if (response.ok && data?.success !== false) {
        return {
          ok: true,
          status: response.status,
          message: data?.message || "Te enviamos el ticket a tu correo.",
        };
      }
      return {
        ok: false,
        status: response.status,
        message: friendlyApiError(
          data,
          response.status === 429
            ? "Ya solicitaste el envío hace poco. Inténtalo más tarde."
            : "No pudimos enviar el ticket. Inténtalo de nuevo."
        ),
      };
    } catch (err) {
      console.error("[sendBetTicketEmail] Network error:", err);
      return {
        ok: false,
        status: 0,
        message: "Error de conexión. Verifica tu internet e inténtalo de nuevo.",
      };
    }
  },

  getGanancias: async () => {
    const response = await fetchWithAuth(`${API_ROOT}/lottery/bets/user_ganancias/`);
    const data = await parseJsonSafe(response);
    return { ok: response.ok, data };
  },

  getHistoricGanancias: async () => {
    const response = await fetchWithAuth(`${API_ROOT}/lottery/bets/historic_ganancias/`);
    const data = await parseJsonSafe(response);
    return { ok: response.ok, data };
  },
};

// ================== MARKETING ==================

export const marketingService = {
  /**
   * Banners administrables (admin → Marketing → Banners).
   * GET /api/marketing/banners/?position=HOME_TOP|REGISTER → [{id, title, image_url,
   * image_mobile_url, link, position, priority}] (solo activos y vigentes, por prioridad).
   */
  getBanners: async (position) => {
    try {
      const qs = position ? `?position=${encodeURIComponent(position)}` : "";
      const response = await fetch(`${API_ROOT}/marketing/banners/${qs}`, { cache: "no-store" });
      const data = await parseJsonSafe(response);
      const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
      return { ok: response.ok, data: response.ok ? list.filter((b) => b && b.image_url) : [] };
    } catch (err) {
      console.error("[getBanners] Network error:", err);
      return { ok: false, data: [] };
    }
  },

  getDefaultLanding: async () => {
    try {
      const response = await fetch(`${API_ROOT}/marketing/landings/default/`);
      const data = await parseJsonSafe(response);
      return { ok: response.ok, status: response.status, data };
    } catch (err) {
      console.error("[getDefaultLanding] Network error:", err);
      return { ok: false, status: 0, data: null };
    }
  },

  getLandingBySlug: async (slug) => {
    try {
      const response = await fetch(
        `${API_ROOT}/marketing/landings/${encodeURIComponent(slug)}/`
      );
      const data = await parseJsonSafe(response);
      return { ok: response.ok, status: response.status, data };
    } catch (err) {
      console.error("[getLandingBySlug] Network error:", err);
      return { ok: false, status: 0, data: null };
    }
  },

  trackRegistration: async (slug, payload = {}) => {
    try {
      const response = await fetch(
        `${API_ROOT}/marketing/landings/${encodeURIComponent(slug)}/track_registration/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await parseJsonSafe(response);
      return { ok: response.ok, status: response.status, data };
    } catch (err) {
      console.error("[trackRegistration] Network error:", err);
      return { ok: false, status: 0, data: null };
    }
  },

  trackDownload: async (slug, payload = {}) => {
    try {
      const response = await fetch(
        `${API_ROOT}/marketing/landings/${encodeURIComponent(slug)}/track_download/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await parseJsonSafe(response);
      return { ok: response.ok, status: response.status, data };
    } catch (err) {
      console.error("[trackDownload] Network error:", err);
      return { ok: false, status: 0, data: null };
    }
  },
};

// ================== BLOG (público, administrado desde el admin Django) ==================

export const blogService = {
  /**
   * GET /api/marketing/blog/?limit=&page=
   * → { success, data: [{ slug, title, excerpt, cover_image, cover_image_mobile,
   *      published_at, category, read_minutes }], count }
   * Nunca lanza: ante error devuelve lista vacía.
   */
  list: async ({ limit, page } = {}) => {
    try {
      const response = await fetch(
        `${API_ROOT}/marketing/blog/${buildQuery({ limit, page })}`,
        { headers: { Accept: "application/json" } }
      );
      const body = await parseJsonSafe(response);
      const list = Array.isArray(body?.data)
        ? body.data
        : Array.isArray(body?.results)
          ? body.results
          : Array.isArray(body)
            ? body
            : [];
      return {
        ok: response.ok,
        status: response.status,
        data: response.ok ? list.filter((p) => p && p.slug) : [],
        count: Number(body?.count) || list.length,
      };
    } catch (err) {
      console.error("[blogService.list] Network error:", err);
      return { ok: false, status: 0, data: [], count: 0 };
    }
  },

  /**
   * GET /api/marketing/blog/{slug}/
   * → { success, data: { slug, title, subtitle, excerpt, cover_image, published_at,
   *      category, read_minutes, body_html, cta_text, cta_url, seo_title, seo_description } }
   */
  get: async (slug) => {
    try {
      const response = await fetch(
        `${API_ROOT}/marketing/blog/${encodeURIComponent(slug)}/`,
        { headers: { Accept: "application/json" } }
      );
      const body = await parseJsonSafe(response);
      const post =
        body && typeof body === "object" && body.data && typeof body.data === "object"
          ? body.data
          : body;
      return {
        ok: response.ok && Boolean(post?.slug || post?.title),
        status: response.status,
        data: response.ok ? post : null,
      };
    } catch (err) {
      console.error("[blogService.get] Network error:", err);
      return { ok: false, status: 0, data: null };
    }
  },
};

// ================== PROMOCIONES / RIFAS ==================

const CONNECTION_ERROR_MESSAGE =
  "Error de conexión. Verifica tu internet e inténtalo de nuevo.";

/** Desempaqueta `{ success, data }` (o devuelve el cuerpo tal cual). */
function unwrapData(body) {
  return body && typeof body === "object" && body.data && typeof body.data === "object"
    ? body.data
    : body;
}

export const raffleService = {
  /**
   * GET /api/raffles/?status=active|upcoming|inactive|all&kind=RAFFLE|PROMO_INFO (público)
   * → { success, data: [...], count, feature_disabled }
   * Con el módulo apagado llega `data: []` y `feature_disabled: true`. Nunca lanza.
   */
  list: async ({ status, kind } = {}) => {
    try {
      const response = await fetch(`${API_ROOT}/raffles/${buildQuery({ status, kind })}`, {
        headers: { Accept: "application/json" },
      });
      const body = await parseJsonSafe(response);
      const list = Array.isArray(body?.data) ? body.data : [];
      return {
        ok: response.ok,
        status: response.status,
        data: response.ok ? list.filter((r) => r && r.slug) : [],
        count: Number(body?.count) || list.length,
        featureDisabled: Boolean(body?.feature_disabled),
      };
    } catch (err) {
      console.error("[raffleService.list] Network error:", err);
      return { ok: false, status: 0, data: [], count: 0, featureDisabled: false };
    }
  },

  /**
   * GET /api/raffles/{slug}/ — público; con token incluye `my_tickets`.
   * 404 + `feature_disabled: true` cuando el módulo está apagado.
   */
  get: async (slug) => {
    const url = `${API_ROOT}/raffles/${encodeURIComponent(slug)}/`;
    try {
      const options = { headers: { Accept: "application/json" }, cache: "no-store" };
      const response = getToken() ? await fetchWithAuth(url, options) : await fetch(url, options);
      const body = await parseJsonSafe(response);
      const raffle = unwrapData(body);
      return {
        ok: response.ok && Boolean(raffle?.slug),
        status: response.status,
        data: response.ok ? raffle : null,
        featureDisabled: Boolean(body?.feature_disabled),
      };
    } catch (err) {
      console.error("[raffleService.get] Network error:", err);
      return { ok: false, status: 0, data: null, featureDisabled: false };
    }
  },

  /**
   * POST /api/raffles/{slug}/buy/ {quantity} con `Idempotency-Key`.
   * Reintentar con la misma clave devuelve el mismo comprobante sin volver a cobrar.
   * Error → { ok: false, code, message, extra } (nada se cobra).
   */
  buy: async (slug, quantity, idempotencyKey) => {
    try {
      const response = await fetchWithAuth(
        `${API_ROOT}/raffles/${encodeURIComponent(slug)}/buy/`,
        {
          method: "POST",
          headers: { "Idempotency-Key": idempotencyKey },
          body: JSON.stringify({ quantity }),
        }
      );
      const body = await parseJsonSafe(response);
      if (response.ok && body?.success !== false) {
        return { ok: true, status: response.status, data: unwrapData(body) };
      }
      return {
        ok: false,
        status: response.status,
        code: typeof body?.code === "string" ? body.code : "",
        message: friendlyApiError(body, "No pudimos completar la compra."),
        extra: body && typeof body === "object" ? body : {},
      };
    } catch (err) {
      console.error("[raffleService.buy] Network error:", err);
      return { ok: false, status: 0, code: "network", message: CONNECTION_ERROR_MESSAGE, extra: {} };
    }
  },

  /** GET /api/raffles/mine/ — mis boletas agrupadas por rifa (funciona con el módulo apagado). */
  mine: async () => {
    try {
      const response = await fetchWithAuth(`${API_ROOT}/raffles/mine/`, { cache: "no-store" });
      const body = await parseJsonSafe(response);
      const list = Array.isArray(body?.data) ? body.data : [];
      return {
        ok: response.ok,
        status: response.status,
        data: response.ok ? list : [],
        message: response.ok ? "" : friendlyApiError(body, "No pudimos cargar tus rifas."),
      };
    } catch (err) {
      console.error("[raffleService.mine] Network error:", err);
      return { ok: false, status: 0, data: [], message: CONNECTION_ERROR_MESSAGE };
    }
  },

  /** GET /api/raffles/receipts/{code}/ — comprobante en JSON (solo el dueño). */
  getReceipt: async (code) => {
    try {
      const response = await fetchWithAuth(
        `${API_ROOT}/raffles/receipts/${encodeURIComponent(code)}/`
      );
      const body = await parseJsonSafe(response);
      return {
        ok: response.ok,
        status: response.status,
        data: response.ok ? unwrapData(body) : null,
        message: response.ok ? "" : friendlyApiError(body, "No pudimos cargar el comprobante."),
      };
    } catch (err) {
      console.error("[raffleService.getReceipt] Network error:", err);
      return { ok: false, status: 0, data: null, message: CONNECTION_ERROR_MESSAGE };
    }
  },

  /** GET /api/raffles/receipts/{code}/image/ → PNG del comprobante (errores en JSON). */
  getReceiptImage: async (code) => {
    try {
      const response = await fetchWithAuth(
        `${API_ROOT}/raffles/receipts/${encodeURIComponent(code)}/image/`,
        { headers: { Accept: "image/png" }, cache: "no-store" }
      );
      const contentType = response.headers.get("content-type") || "";
      if (response.ok && contentType.startsWith("image/")) {
        const blob = await response.blob();
        return { ok: true, status: response.status, blob };
      }
      const data = await parseJsonSafe(response);
      return {
        ok: false,
        status: response.status,
        blob: null,
        error: friendlyApiError(
          data,
          response.ok
            ? "La respuesta del servidor no es una imagen válida."
            : "No pudimos cargar el comprobante."
        ),
      };
    } catch (err) {
      console.error("[raffleService.getReceiptImage] Network error:", err);
      return { ok: false, status: 0, blob: null, error: CONNECTION_ERROR_MESSAGE };
    }
  },

  /** POST /api/raffles/receipts/{code}/send/ → envía el PNG al correo (máx. 3/hora → 429). */
  sendReceiptEmail: async (code) => {
    try {
      const response = await fetchWithAuth(
        `${API_ROOT}/raffles/receipts/${encodeURIComponent(code)}/send/`,
        { method: "POST" }
      );
      const data = await parseJsonSafe(response);
      if (response.ok && data?.success !== false) {
        return {
          ok: true,
          status: response.status,
          message: data?.message || "Te enviamos el comprobante a tu correo.",
        };
      }
      return {
        ok: false,
        status: response.status,
        message: friendlyApiError(
          data,
          response.status === 429
            ? "Ya solicitaste el envío varias veces. Inténtalo más tarde."
            : "No pudimos enviar el comprobante. Inténtalo de nuevo."
        ),
      };
    } catch (err) {
      console.error("[raffleService.sendReceiptEmail] Network error:", err);
      return { ok: false, status: 0, message: CONNECTION_ERROR_MESSAGE };
    }
  },

  /** GET /api/raffles/{slug}/terms/?version=N (público). Sin versión → la vigente. */
  getTerms: async (slug, version) => {
    try {
      const response = await fetch(
        `${API_ROOT}/raffles/${encodeURIComponent(slug)}/terms/${buildQuery({ version })}`,
        { headers: { Accept: "application/json" } }
      );
      const body = await parseJsonSafe(response);
      const terms = unwrapData(body);
      return {
        ok: response.ok && Boolean(terms?.terms_html !== undefined || terms?.slug),
        status: response.status,
        data: response.ok ? terms : null,
      };
    } catch (err) {
      console.error("[raffleService.getTerms] Network error:", err);
      return { ok: false, status: 0, data: null };
    }
  },
};

// ================== PAYMENT (PayCash) ==================

/** Extrae payload `data` cuando el backend responde `{ success, data }`. */
function unwrapPaymentData(body) {
  if (!body || typeof body !== "object") return body;
  if (body.data != null && (body.success === true || body.success === undefined)) {
    return body.data;
  }
  return body;
}

/** Lista de historial: cruda, `{ data: [] }` o `{ data: { results|transactions|items|recargas } }`. */
function parseRechargeHistoryList(body) {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return [];

  const payload = body.data !== undefined ? body.data : body;
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    for (const key of ["results", "transactions", "items", "recargas"]) {
      if (Array.isArray(payload[key])) return payload[key];
    }
  }
  return [];
}

export const paymentService = {
  /** Legacy PlaceToPay / Wompi — no usar para recargas PayCash. */
  initTransaction: async (payload) => {
    const response = await fetchWithAuth(`${API_ROOT}/payment/init_transaction/`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    const data = await parseJsonSafe(response);
    return { ok: response.ok, data: data || {} };
  },

  processPayment: async (paymentData) => {
    const response = await fetchWithAuth(`${API_ROOT}/payment/init_transaction/`, {
      method: "POST",
      body: JSON.stringify(paymentData),
    });
    const data = await parseJsonSafe(response);
    return { ok: response.ok, data: data || {} };
  },

  /**
   * Crea recarga PayCash.
   * @param {{ monto: number, descripcion?: string }} payload — monto en pesos
   */
  createRecharge: async ({ monto, descripcion = "Recarga de saldo" }) => {
    const response = await fetchWithAuth(`${API_ROOT}/payment/create_recharge/`, {
      method: "POST",
      body: JSON.stringify({ monto, descripcion }),
    });
    const body = await parseJsonSafe(response);
    if (response.ok) {
      return { ok: true, data: unwrapPaymentData(body) || {}, raw: body };
    }
    return { ok: false, data: body || {}, status: response.status };
  },

  /** Consulta / reconcilia estado de una recarga. */
  getPaymentStatus: async (transactionId) => {
    const response = await fetchWithAuth(
      `${API_ROOT}/payment/${encodeURIComponent(transactionId)}/payment_status/`,
      { method: "GET" }
    );
    const body = await parseJsonSafe(response);
    if (response.ok) {
      return { ok: true, data: unwrapPaymentData(body) || {}, raw: body };
    }
    return { ok: false, data: body || {}, status: response.status };
  },

  /**
   * Cancela recarga pendiente.
   * 409 + status APPROVED → alreadyApproved (pago confirmado).
   */
  cancelRecharge: async (transactionId) => {
    const response = await fetchWithAuth(
      `${API_ROOT}/payment/${encodeURIComponent(transactionId)}/cancel_recharge/`,
      { method: "POST" }
    );
    const body = await parseJsonSafe(response);
    const payload = unwrapPaymentData(body);
    const status =
      payload && typeof payload === "object"
        ? String(payload.status || "").toUpperCase()
        : "";

    if (response.ok) {
      return {
        ok: true,
        outcome: "cancelled",
        data: payload || {},
        message:
          (payload && payload.message) ||
          "Recarga cancelada correctamente.",
      };
    }

    if (response.status === 409 && status === "APPROVED") {
      return {
        ok: true,
        outcome: "alreadyApproved",
        data: payload || {},
        message:
          (payload && payload.message) ||
          "La recarga ya fue pagada; el saldo fue acreditado.",
      };
    }

    return {
      ok: false,
      outcome: response.status === 502 ? "paycashError" : "error",
      data: body || {},
      status: response.status,
      message: formatApiError(body),
    };
  },

  /** Historial de recargas del usuario autenticado. */
  getHistory: async () => {
    const response = await fetchWithAuth(`${API_ROOT}/payment/history/`, {
      method: "GET",
    });
    const body = await parseJsonSafe(response);
    if (response.ok) {
      return {
        ok: true,
        data: parseRechargeHistoryList(body),
        raw: body,
      };
    }
    return { ok: false, data: [], raw: body || {}, status: response.status };
  },

  /**
   * Solicitar retiro — espejo de Flutter PaymentService.retiro.
   * POST /payment/request_withdrawal/
   */
  requestWithdrawal: async ({
    user,
    amount,
    bank,
    account_type,
    account_number,
    use_keys,
  }) => {
    const idempotencyKey = generateIdempotencyKey();
    const response = await fetchWithAuth(
      `${API_ROOT}/payment/request_withdrawal/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify({
          user,
          amount,
          bank,
          account_type,
          account_number,
          use_keys,
          idempotency_key: idempotencyKey,
        }),
      }
    );
    const body = await parseJsonSafe(response);
    if (response.ok) {
      return { ok: true, status: response.status, data: body || {} };
    }
    return mapWithdrawalError(body, response.status);
  },

  /** Historial de retiros del usuario autenticado. */
  getWithdrawalHistory: async () => {
    const response = await fetchWithAuth(
      `${API_ROOT}/payment/withdrawal_history/`,
      { method: "GET" }
    );
    const body = await parseJsonSafe(response);
    if (response.ok) {
      const withdrawals = Array.isArray(body?.withdrawals)
        ? body.withdrawals
        : Array.isArray(body?.results)
          ? body.results
          : Array.isArray(body)
            ? body
            : [];
      return {
        ok: true,
        data: {
          withdrawals,
          summary: body?.summary || null,
        },
        raw: body,
      };
    }
    return {
      ok: false,
      data: { withdrawals: [], summary: null },
      raw: body || {},
      status: response.status,
      message: formatApiError(body),
    };
  },
};

// ================== BONOS, CÓDIGOS PROMO Y REFERIDOS ==================

/** Código de error del backend (`{ error: { code } }` o `{ code }`), o "". */
export function apiErrorCode(data) {
  if (!data || typeof data !== "object") return "";
  if (data.error && typeof data.error === "object" && data.error.code) {
    return String(data.error.code);
  }
  return data.code ? String(data.code) : "";
}

/** Normaliza el resultado de un POST/GET de este bloque (nunca lanza). */
async function requestJson(url, options = {}, { auth = true } = {}) {
  try {
    const response =
      auth || getToken()
        ? await fetchWithAuth(url, options)
        : await fetch(url, {
            ...options,
            headers: { Accept: "application/json", ...(options.headers || {}) },
          });
    const body = await parseJsonSafe(response);
    const ok = response.ok && body?.success !== false;
    return {
      ok,
      status: response.status,
      body: body || {},
      data: unwrapData(body) || {},
      code: ok ? "" : apiErrorCode(body),
      message: ok
        ? (typeof body?.message === "string" && body.message) || ""
        : friendlyApiError(body, "No pudimos completar la solicitud. Inténtalo de nuevo."),
    };
  } catch (err) {
    console.error("[requestJson] Network error:", url, err);
    return {
      ok: false,
      status: 0,
      body: {},
      data: {},
      code: "",
      message: CONNECTION_ERROR_MESSAGE,
    };
  }
}

export const bonusService = {
  /**
   * GET /api/bonuses/mine/ → { available_count, available: [...], history: [...] }
   * Cada bono: { id, kind: FRACTION|TICKET, label, source, source_display, status,
   *   status_display, issued_at, expires_at, days_left, used_at, bet_id, all_lotteries, lotteries }
   */
  mine: async () => {
    const res = await requestJson(`${API_ROOT}/bonuses/mine/`, { cache: "no-store" });
    const d = res.data || {};
    return {
      ok: res.ok,
      status: res.status,
      message: res.message,
      available: Array.isArray(d.available) ? d.available.filter((b) => b && b.id) : [],
      history: Array.isArray(d.history) ? d.history.filter((b) => b && b.id) : [],
    };
  },

  /**
   * GET /api/bonuses/welcome/ (público; con sesión trae `eligible`).
   * → { active, eligible, name, fraction_min_amount, ticket_min_amount, validity_days,
   *     all_lotteries, lotteries }
   */
  welcome: async () => {
    const res = await requestJson(
      `${API_ROOT}/bonuses/welcome/`,
      { cache: "no-store" },
      { auth: false }
    );
    return { ok: res.ok, data: res.ok ? res.data : null };
  },
};

export const promoService = {
  /**
   * POST /api/discounts/promo/validate/ — vista previa (no consume nada).
   * Con `bets` (forma de create_bet, con `bonus_id`) devuelve `order` con el cobro exacto.
   * `applies_in`: CHECKOUT (descuento en la compra) | REDEEM (canjear en /redeem/).
   */
  validate: async ({ code, amount, bets } = {}) => {
    const payload = { code: String(code || "").trim().toUpperCase() };
    const numericAmount = Number(amount);
    if (Number.isFinite(numericAmount) && numericAmount > 0) {
      payload.amount = numericAmount.toFixed(2);
    }
    if (Array.isArray(bets) && bets.length > 0) payload.bets = bets;
    return requestJson(`${API_ROOT}/discounts/promo/validate/`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  /**
   * POST /api/discounts/promo/redeem/ — saldo de regalo (acredita saldo) o boleto
   * gratis (crea un bono). Los descuentos responden GL-PRM-014 (se usan al pagar).
   */
  redeem: async (code) =>
    requestJson(`${API_ROOT}/discounts/promo/redeem/`, {
      method: "POST",
      body: JSON.stringify({ code: String(code || "").trim().toUpperCase() }),
    }),
};

export const referralService = {
  /** POST /api/discounts/referrals/apply/ — ingresar el código de un amigo tras el registro. */
  apply: async (code) =>
    requestJson(`${API_ROOT}/discounts/referrals/apply/`, {
      method: "POST",
      body: JSON.stringify({ code: String(code || "").trim().toUpperCase() }),
    }),

  /** GET /api/discounts/referrals/my-code/ */
  myCode: async () =>
    requestJson(`${API_ROOT}/discounts/referrals/my-code/`, { cache: "no-store" }),

  /** GET /api/discounts/referrals/dashboard/ */
  dashboard: async () =>
    requestJson(`${API_ROOT}/discounts/referrals/dashboard/`, { cache: "no-store" }),
};

// ================== UTILS ==================

export const formatCurrency = (amount) => {
  const num = parseFloat(amount);
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number.isFinite(num) ? num : 0);
};

export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("es-CO", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const formatShortDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/** Mensaje legible — alineado con Flutter `ApiErrorParser.parse` */
export function formatApiError(data) {
  if (!data) return "Ocurrió un error";
  if (typeof data === "string") return data;
  if (data.error && typeof data.error === "object") {
    const details = data.error.details;
    if (details && typeof details === "string" && details.trim()) {
      return details.trim();
    }
    const message = data.error.message;
    if (message && typeof message === "string" && message.trim()) {
      return message;
    }
  }
  if (data.detail) return Array.isArray(data.detail) ? data.detail.join(", ") : String(data.detail);
  if (data.message && typeof data.message === "string") return data.message;
  if (data.error && typeof data.error === "string") return data.error;
  if (data.details && typeof data.details === "string") return data.details;
  const parts = [];
  Object.entries(data).forEach(([key, val]) => {
    if (val == null || key === "success" || key === "timestamp" || key === "request_id") return;
    if (Array.isArray(val)) parts.push(`${key}: ${val.join(", ")}`);
    else if (typeof val === "object") {
      if (val.message) parts.push(String(val.message));
      else parts.push(`${key}: ${JSON.stringify(val)}`);
    } else parts.push(`${key}: ${val}`);
  });
  return parts.length ? parts.join(" · ") : "Ocurrió un error";
}

/** Nombre con extensión aceptada por el backend (Textract: JPEG/PNG). */
function identityUploadName(file, label) {
  const type = String(file?.type || "").toLowerCase();
  return `${label}.${type === "image/png" ? "png" : "jpg"}`;
}

/** Mensaje legible para errores de verify_identity (usa `error.details` del backend). */
function formatIdentityError(body, status) {
  if (status === 413) {
    return "Las fotos son demasiado pesadas. Cada una debe pesar máximo 5 MB.";
  }
  const hasEnvelope = body && typeof body === "object" && !body.raw;
  if (!hasEnvelope) {
    if (status >= 500 || status === 0) {
      return "No pudimos procesar tu cédula en este momento. Intenta de nuevo en unos minutos.";
    }
    return "No fue posible verificar tu identidad. Intenta de nuevo.";
  }
  const msg = formatApiError(body);
  if (!msg || msg === "Ocurrió un error") {
    return "No fue posible verificar tu identidad. Intenta de nuevo.";
  }
  return msg;
}

function mapWithdrawalError(body, status) {
  const raw = body?.error;
  if (raw && typeof raw === "object") {
    const code = String(raw.code || "");
    const details = String(raw.details || "").trim();
    const message = String(raw.message || "").trim();
    const text = details || message;

    if (code === "GL-PAY-014") {
      const extra = raw.extra && typeof raw.extra === "object" ? raw.extra : {};
      return {
        ok: false,
        status,
        code: "GL-PAY-014",
        message: text || "El monto excede el límite de retiro automático",
        supportContact: extra.support_contact || null,
        data: body || {},
      };
    }

    const looksUnverified =
      code === "GL-USR-013" ||
      code === "identity_not_verified" ||
      /verificar|identidad/i.test(text);
    if (status === 403 && looksUnverified) {
      return {
        ok: false,
        status,
        code: "identity_not_verified",
        message:
          text || "Debes verificar tu identidad para solicitar un retiro.",
        data: body || {},
      };
    }
  }

  if (
    typeof raw === "string" &&
    (raw === "identity_not_verified" || /identidad/i.test(raw))
  ) {
    return {
      ok: false,
      status,
      code: "identity_not_verified",
      message: body?.message || raw,
      data: body || {},
    };
  }

  if (status === 403) {
    const parsed = formatApiError(body);
    if (/verificar|identidad/i.test(parsed)) {
      return {
        ok: false,
        status,
        code: "identity_not_verified",
        message: parsed,
        data: body || {},
      };
    }
  }

  return {
    ok: false,
    status,
    code: null,
    message: formatApiError(body),
    data: body || {},
  };
}

/**
 * Buzón de notificaciones del usuario (campanita del header).
 * GET /api/notifications/ · GET unread_count/ · POST {id}/read/ · POST read_all/
 */
export const notificationService = {
  list: async ({ pageSize = 20 } = {}) => {
    try {
      const response = await fetchWithAuth(
        `${API_ROOT}/notifications/?page_size=${encodeURIComponent(pageSize)}`,
        { cache: "no-store" }
      );
      const body = await parseJsonSafe(response);
      return {
        ok: response.ok,
        data: response.ok && Array.isArray(body?.data) ? body.data : [],
        message: response.ok ? "" : friendlyApiError(body, "No pudimos cargar tus notificaciones."),
      };
    } catch (err) {
      console.error("[notificationService.list] Network error:", err);
      return { ok: false, data: [], message: CONNECTION_ERROR_MESSAGE };
    }
  },

  unreadCount: async () => {
    try {
      const response = await fetchWithAuth(`${API_ROOT}/notifications/unread_count/`, { cache: "no-store" });
      const body = await parseJsonSafe(response);
      return { ok: response.ok, count: Number(body?.data?.unread_count) || 0 };
    } catch (err) {
      return { ok: false, count: 0 };
    }
  },

  markRead: async (id) => {
    try {
      const response = await fetchWithAuth(
        `${API_ROOT}/notifications/${encodeURIComponent(id)}/read/`,
        { method: "POST" }
      );
      return { ok: response.ok };
    } catch (err) {
      return { ok: false };
    }
  },

  markAllRead: async () => {
    try {
      const response = await fetchWithAuth(`${API_ROOT}/notifications/read_all/`, { method: "POST" });
      return { ok: response.ok };
    } catch (err) {
      return { ok: false };
    }
  },
};

/** Mensaje genérico en login: no revelar si falló el PIN, el teléfono, etc. */
export const LOGIN_GENERIC_ERROR_MESSAGE =
  "Datos incorrectos. Intenta de nuevo.";

export function formatLoginError(_data) {
  return LOGIN_GENERIC_ERROR_MESSAGE;
}

const api = {
  authService,
  userService,
  lotteryService,
  marketingService,
  raffleService,
  paymentService,
  bonusService,
  promoService,
  referralService,
  notificationService,
  formatCurrency,
  formatDate,
  formatShortDate,
  formatApiError,
  formatLoginError,
  LOGIN_GENERIC_ERROR_MESSAGE,
};

export default api;
